// Types for the project subsystems (issues/sprints/board, blog, chat, cross-project activity).
// Mirrors the BFF's /api/projects/{pid}/issues|sprints|board|posts|chat, /api/issues, /api/sprints,
// /api/posts, /api/chat, and /api/account/activity|notifications JSON contracts.

export type IssueType = "EPIC" | "STORY" | "TASK" | "BUG" | "SUBTASK" | "REQUIREMENT" | "DESIGN" | "TEST_CLASS" | "TEST_ITEM";
export type IssueStatus = "BACKLOG" | "TODO" | "IN_PROGRESS" | "IN_REVIEW" | "DONE";
export type IssueStatusCategory = "TODO" | "IN_PROGRESS" | "DONE";
export type IssuePriority = "HIGHEST" | "HIGH" | "MEDIUM" | "LOW" | "LOWEST";
export type IssueResolution = "UNRESOLVED" | "DONE" | "WONT_DO" | "DUPLICATE" | "CANNOT_REPRODUCE";
export type IssueActivityType =
  | "CREATED"
  | "STATUS_CHANGED"
  | "ASSIGNEE_CHANGED"
  | "PRIORITY_CHANGED"
  | "TYPE_CHANGED"
  | "POINTS_CHANGED"
  | "SPRINT_CHANGED"
  | "RESOLVED"
  | "REOPENED"
  | "COMMENTED";

export type SprintStatus = "FUTURE" | "ACTIVE" | "CLOSED";

export type PostStatus = "DRAFT" | "PUBLISHED" | "ARCHIVED";

export type ChatChannelType = "DIRECT" | "GROUP";

export type NotificationType =
  | "ISSUE_ASSIGNED"
  | "ISSUE_MENTIONED"
  | "ISSUE_COMMENTED"
  | "POST_MENTIONED"
  | "POST_COMMENTED"
  | "POST_REPLY"
  | "CHAT_ADDED";

/** Maps an {@link IssueStatus} to its board column category. */
export const ISSUE_STATUS_CATEGORY: Record<IssueStatus, IssueStatusCategory> = {
  BACKLOG: "TODO",
  TODO: "TODO",
  IN_PROGRESS: "IN_PROGRESS",
  IN_REVIEW: "IN_PROGRESS",
  DONE: "DONE",
};

// ---- Issues ----

export interface Issue {
  id: string;
  projectId: string;
  number: number;
  key: string;
  type: IssueType;
  status: IssueStatus;
  priority: IssuePriority;
  summary: string;
  description?: string | null;
  reporterId: string;
  assigneeId?: string | null;
  epicId?: string | null;
  parentId?: string | null;
  sprintId?: string | null;
  storyPoints?: number | null;
  dueDate?: string | null; // ISO date (yyyy-MM-dd)
  resolution: IssueResolution;
  labels: string[];
  watcherIds: string[];
  rank: string;
  /** The work areas this issue belongs to (0..n). Values are {@link WorkAreaType}. */
  workAreaTypes: string[];
  createdBy: string;
  createdAt: string;
}

export interface CreateIssueRequest {
  type: IssueType;
  summary: string;
  description?: string;
  priority?: IssuePriority;
  assigneeId?: string;
  parentId?: string;
  epicId?: string;
  sprintId?: string;
  storyPoints?: number;
  dueDate?: string;
  labels?: string[];
  workAreaTypes?: string[];
}

/** Body for PUT /api/issues/{id}/work-areas — replaces the issue's work-area set. */
export interface SetWorkAreasRequest {
  workAreaTypes: string[];
}

export interface UpdateIssueRequest {
  summary?: string;
  description?: string;
  priority?: IssuePriority;
  dueDate?: string;
}

export interface IssueComment {
  id: string;
  issueId: string;
  authorId: string;
  body: string;
  mentionedUserIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AddIssueCommentRequest {
  body: string;
  mentionedUserIds?: string[];
}

export interface IssueActivity {
  id: string;
  issueId: string;
  projectId: string;
  sprintId?: string | null;
  actorId: string;
  type: IssueActivityType;
  field?: string | null;
  oldValue?: string | null;
  newValue?: string | null;
  createdAt: string;
}

// ---- Sprints / board ----

export interface Sprint {
  id: string;
  projectId: string;
  name: string;
  goal?: string | null;
  status: SprintStatus;
  startDate?: string | null;
  endDate?: string | null;
  completedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateSprintRequest {
  name: string;
  goal?: string;
  startDate?: string;
  endDate?: string;
}

export interface UpdateSprintRequest {
  name?: string;
  goal?: string;
  startDate?: string;
  endDate?: string;
}

export interface StartSprintRequest {
  startDate?: string;
  endDate?: string;
}

export interface BurndownPoint {
  date: string; // ISO date
  remaining: number;
  ideal: number;
}

export interface BurndownChart {
  sprintId: string;
  sprintName: string;
  startDate: string;
  endDate: string;
  totalPoints: number;
  points: BurndownPoint[];
}

// ---- Blog ----

export interface Post {
  id: string;
  projectId: string;
  authorId: string;
  title: string;
  body: string; // markdown
  status: PostStatus;
  tags: string[];
  mentionedUserIds: string[];
  publishedAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePostRequest {
  title: string;
  body: string;
  tags?: string[];
  mentionedUserIds?: string[];
  publishNow: boolean;
}

export interface UpdatePostRequest {
  title?: string;
  body?: string;
  tags?: string[];
  mentionedUserIds?: string[];
}

export interface PostComment {
  id: string;
  postId: string;
  authorId: string;
  parentCommentId?: string | null;
  body: string;
  mentionedUserIds: string[];
  createdAt: string;
  updatedAt: string;
}

export interface AddPostCommentRequest {
  parentCommentId?: string;
  body: string;
  mentionedUserIds?: string[];
}

// ---- Chat ----

export interface ChatChannel {
  id: string;
  projectId?: string | null;
  workspaceId?: string | null;
  type: ChatChannelType;
  name?: string | null;
  topic?: string | null;
  createdBy: string;
  /** Null until someone posts — a freshly created channel has no messages. */
  lastMessageAt?: string | null;
  /** Participants. A DIRECT channel has no name, so this is what labels it with the other person. */
  memberIds?: string[];
}

export interface CreateGroupChannelRequest {
  name: string;
  topic?: string;
  memberIds: string[];
}

export interface OpenDirectChannelRequest {
  otherUserId: string;
}

export interface ChatMember {
  id: string;
  channelId: string;
  userId: string;
  lastReadAt?: string | null;
  joinedAt: string;
}

export interface ChatMessage {
  id: string;
  channelId: string;
  senderId: string;
  body: string;
  mentionedUserIds: string[];
  edited: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface SendMessageRequest {
  body: string;
  mentionedUserIds?: string[];
}

/** One realtime frame pushed over /ws/chat for a newly-sent message. */
export interface ChatMessageNotification {
  channelId: string;
  projectId?: string | null;
  messageId: string;
  senderId: string;
  body: string;
  createdAt: string;
}

// ---- Cross-project activity ----

export interface Notification {
  id: string;
  type: NotificationType;
  actorId?: string | null;
  projectId?: string | null;
  sourceType?: string | null;
  sourceId?: string | null;
  title: string;
  read: boolean;
  createdAt: string;
}

export interface MyActivity {
  myIssues: Issue[];
  mentionedPosts: Post[];
  mentionedInComments: PostComment[];
  myPosts: Post[];
  activityOnMyPosts: PostComment[];
  myChats: ChatChannel[];
}
